import { Spinner } from '../components/feedback';
import { EditBoundary, useEditDirty } from '../components/EditBoundary';
import { normalizeVirtualPath } from '../../../../packages/core/src/virtual-path';
import { SftpConnection } from './SftpSection';
// ファイルの閲覧・編集・アップロード。見せるのは設定・プラグイン・データパックなどの決まった場所だけ。
// 削除は /data/.trash へ移すだけ（元に戻せる）。起動中に編集できない場所は理由を表示する。
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { apiM } from '../api/client-maint';
import type { ServerView } from '../api/types';
import type { FileContent, FileEntry, FileRootInfo } from '../api/types-maint';
import { errorMessage, useApp } from '../app-context';
import { Badge, Empty, ErrorAlert } from '../components/ui';
import { formatBytes, formatDateTime } from '../format';
import { usePoll } from '../hooks/usePoll';

export type FilesApi = Pick<
  typeof apiM,
  'fileRoots' | 'listFiles' | 'readFile' | 'writeFile' | 'makeDirectory' | 'trashFile' | 'uploadFile'
>;
const MAX_UPLOAD = 64 * 1024 * 1024;
const NAME = /^[^/\\:*?"<>|\0]{1,120}$/;
const join = (dir: string, name: string) => normalizeVirtualPath(`${dir}/${name}`);
const parent = (path: string) => path.split('/').slice(0, -1).join('/');

const kindInfo: Record<FileRootInfo['kind'], { label: string; tone: 'ok' | 'warn' | 'muted' }> = {
  live: { label: '起動中も編集可', tone: 'ok' },
  copied: { label: '停止中のみ編集可', tone: 'warn' },
  readonly: { label: '読み取りのみ', tone: 'muted' },
};

function Crumbs({ path, onOpen }: { path: string; onOpen: (path: string) => void }) {
  const parts = path ? path.split('/') : [];
  return (
    <nav className="crumbs" aria-label="場所">
      <button disabled={parts.length === 0} onClick={() => onOpen('')}>
        ファイル
      </button>
      {parts.map((part, index) => {
        const target = parts.slice(0, index + 1).join('/');
        return (
          <span key={target}>
            <span className="faint">/</span>
            <button disabled={index === parts.length - 1} onClick={() => onOpen(target)}>
              {part}
            </button>
          </span>
        );
      })}
    </nav>
  );
}

function EditorContent({
  serverId,
  fileApi = apiM,
  file,
  onClose,
  onSaved,
}: {
  serverId: string;
  fileApi?: FilesApi;
  file: FileContent;
  onClose: () => void;
  onSaved: (file: FileContent) => void;
}) {
  const { canOperate, notify } = useApp();
  const [text, setText] = useState(file.content);
  const [saving, setSaving] = useState(false);
  const dirty = text !== file.content || !file.sha256;
  useEditDirty(dirty);
  const editable = canOperate && file.writable;

  const save = async () => {
    if (!dirty || !editable) return;
    setSaving(true);
    try {
      const { sha256 } = await fileApi.writeFile(serverId, file.path, text, file.sha256 || undefined);
      notify('保存しました');
      onSaved({ ...file, content: text, sha256 });
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const leave = (event: BeforeUnloadEvent) => {
      if (dirty) event.preventDefault();
    };
    window.addEventListener('beforeunload', leave);
    return () => window.removeEventListener('beforeunload', leave);
  }, [dirty]);

  const close = () => {
    if (dirty && !window.confirm('保存していない変更があります。閉じますか？')) return;
    onClose();
  };

  return (
    <section className="card">
      <div className="card-header">
        <div className="row" style={{ gap: 8, minWidth: 0 }}>
          <strong className="mono" style={{ overflowWrap: 'anywhere' }}>
            {file.path}
          </strong>
          {dirty && <Badge tone="warn">未保存</Badge>}
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn small" onClick={close}>
            閉じる
          </button>
          {editable && (
            <button className="btn primary small" disabled={!dirty || saving} onClick={() => void save()}>
              {saving && <Spinner />}
              保存
            </button>
          )}
        </div>
      </div>
      {!file.writable && file.reason && (
        <div className="alert info small" style={{ margin: 12 }}>
          {file.reason}
        </div>
      )}
      <textarea
        className="input file-editor"
        aria-label={`${file.path} の内容`}
        value={text}
        readOnly={!editable}
        spellCheck={false}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 's') {
            e.preventDefault();
            void save();
          }
        }}
        style={{ border: 'none', borderRadius: 0 }}
      />
      {file.path.endsWith('server.properties') && (
        <div className="card-body small faint">
          rcon.password は伏せて表示しています（そのまま保存すれば元の値が残ります）。server-port や online-mode
          など一部の値は起動のたびにパネルが上書きします。
        </div>
      )}
    </section>
  );
}

export function FilesTab({ server }: { server: ServerView }) {
  return <FilesBrowser server={server} />;
}
export function FilesBrowser({
  server,
  fileApi = apiM,
  velocity = false,
}: {
  server: { id: string; state: string };
  fileApi?: FilesApi;
  velocity?: boolean;
}) {
  const { canOperate, notify } = useApp();
  const [path, setPath] = useState('');
  const [open, setOpen] = useState<FileContent>();
  const [busy, setBusy] = useState(false);
  const upload = useRef<HTMLInputElement>(null);
  const roots = usePoll(() => fileApi.fileRoots(server.id), 0, [server.id]);
  const listing = usePoll(() => fileApi.listFiles(server.id, path), 15000, [server.id, path, server.state]);

  const rootInfo = roots.data?.find((r) => r.name === path.split('/')[0]);
  const data = listing.data?.path === path ? listing.data : undefined;

  const openFile = async (entry: FileEntry) => {
    const target = join(path, entry.name);
    if (entry.type === 'directory') {
      setPath(target);
      return;
    }
    if (entry.size > 1024 * 1024) {
      notify('1MB を超えるファイルはここでは開けません', true);
      return;
    }
    try {
      setOpen(await fileApi.readFile(server.id, target));
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const act = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await work();
      notify(done);
      await listing.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const makeDirectory = () => {
    const name = window.prompt('新しいフォルダの名前')?.trim();
    if (!name) return;
    if (!NAME.test(name)) return notify('使えない文字が含まれています', true);
    void act(() => fileApi.makeDirectory(server.id, join(path, name)), 'フォルダを作りました');
  };

  const trash = (entry: FileEntry) => {
    const target = join(path, entry.name);
    const what = entry.type === 'directory' ? 'フォルダ（中身ごと）' : 'ファイル';
    if (
      !window.confirm(
        `${target} の${what}をごみ箱へ移しますか？（${velocity ? '/var/lib/velocity/.trash' : '/data/.trash'} から元に戻せます）`,
      )
    )
      return;
    void act(() => fileApi.trashFile(server.id, target), 'ごみ箱へ移しました');
  };

  const uploadFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_UPLOAD) {
        notify(`${file.name} は 64MB を超えるのでアップロードできません`, true);
        continue;
      }
      const target = join(path, file.name);
      await act(async () => {
        try {
          await fileApi.uploadFile(server.id, target, file);
        } catch (error) {
          if (!(error instanceof ApiError && error.message.includes('同じ名前'))) throw error;
          if (!window.confirm(`${file.name} はすでにあります。上書きしますか？`)) return;
          await fileApi.uploadFile(server.id, target, file, true);
        }
      }, `${file.name} をアップロードしました`);
    }
    if (upload.current) upload.current.value = '';
  };

  if (open) {
    return (
      <Editor
        serverId={server.id}
        inheritedBoundary={velocity}
        fileApi={fileApi}
        file={open}
        onClose={() => {
          setOpen(undefined);
          void listing.reload();
        }}
        onSaved={setOpen}
      />
    );
  }

  return (
    <div className="stack">
      {!velocity && <SftpConnection serverId={server.id} />}
      <section className="card">
        <div className="card-header">
          <Crumbs path={path} onOpen={setPath} />
          {canOperate && data && (path || velocity) && (
            <div className="row" style={{ gap: 6 }}>
              {velocity && data.writable && (
                <button
                  className="btn small"
                  disabled={busy}
                  onClick={() => {
                    const name = window.prompt('新しいファイルの名前')?.trim();
                    if (!name) return;
                    if (!NAME.test(name)) return notify('使えない文字が含まれています', true);
                    if (data.entries.some((entry) => entry.name === name))
                      return notify('同じ名前のファイルがあります', true);
                    setOpen({ path: join(path, name), content: '', sha256: '', writable: true });
                  }}
                >
                  ファイルを作成
                </button>
              )}
              {data.writable && (
                <button className="btn small" disabled={busy} onClick={makeDirectory}>
                  {busy && <Spinner />}
                  フォルダを作成
                </button>
              )}
              {data.upload && (
                <>
                  <button className="btn small" disabled={busy} onClick={() => upload.current?.click()}>
                    {busy && <Spinner />}
                    アップロード
                  </button>
                  <input ref={upload} type="file" multiple hidden onChange={(e) => void uploadFiles(e.target.files)} />
                </>
              )}
            </div>
          )}
        </div>
        <ErrorAlert error={listing.error ?? roots.error} />
        {path && rootInfo && (
          <div className="card-body row small" style={{ gap: 8 }}>
            <Badge tone={kindInfo[rootInfo.kind].tone}>{kindInfo[rootInfo.kind].label}</Badge>
            <span className="muted">{rootInfo.label}</span>
            {data?.reason && <span className="faint">{data.reason}</span>}
          </div>
        )}
        {!velocity && !path && roots.data ? (
          <div className="table-wrap">
            <table>
              <tbody>
                {roots.data.map((root) => (
                  <tr key={root.name} className="file-row" onClick={() => setPath(root.name)}>
                    <td>
                      <span aria-hidden>📁 </span>
                      <button
                        className="link-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPath(root.name);
                        }}
                      >
                        {root.name}
                      </button>
                      <div className="small muted">{root.label}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={kindInfo[root.kind].tone}>{kindInfo[root.kind].label}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : data && data.entries.length === 0 ? (
          <Empty>空のフォルダです。</Empty>
        ) : (
          data && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>名前</th>
                    <th>サイズ</th>
                    <th>更新</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(path.includes('/') || (velocity && path)) && (
                    <tr className="file-row" onClick={() => setPath(parent(path))}>
                      <td colSpan={4} className="muted">
                        <span aria-hidden>↩ </span>上へ
                      </td>
                    </tr>
                  )}
                  {data.entries.map((entry) => (
                    <tr key={entry.name} className="file-row" onClick={() => void openFile(entry)}>
                      <td>
                        <span aria-hidden>{entry.type === 'directory' ? '📁 ' : '📄 '}</span>
                        <button
                          className="link-button mono"
                          onClick={(e) => {
                            e.stopPropagation();
                            void openFile(entry);
                          }}
                        >
                          {entry.name}
                        </button>
                      </td>
                      <td className="small muted">{entry.type === 'file' ? formatBytes(entry.size) : ''}</td>
                      <td className="small faint">{formatDateTime(entry.modifiedAt)}</td>
                      <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        {canOperate && data.writable && (
                          <button className="btn ghost small danger" disabled={busy} onClick={() => trash(entry)}>
                            {busy && <Spinner />}
                            削除
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>
      {velocity ? (
        <p className="small faint">
          秘密情報は開けません。velocity.toml は Panel が管理しているため編集できません。削除したものは
          /var/lib/velocity/.trash に残ります。
        </p>
      ) : (
        <p className="small faint" style={{ margin: 0 }}>
          「停止中のみ編集可」の場所（server.properties
          など）は、起動時にワールド側へ写すため、起動中の編集は反映されません。 アップロードは 1 ファイル 64MB
          まで。削除したものは /data/.trash に残ります。
        </p>
      )}
    </div>
  );
}

function Editor({
  inheritedBoundary,
  ...props
}: Parameters<typeof EditorContent>[0] & { inheritedBoundary?: boolean }) {
  if (inheritedBoundary) return <EditorContent {...props} />;
  return (
    <EditBoundary resource={{ target: `server:${props.serverId}:file:${props.file.path}`, screen: 'file' }}>
      <EditorContent {...props} />
    </EditBoundary>
  );
}
